import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Container } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { getTenantSubdomain, isAutoServicesTenant } from "../../utils/tenantBranding";

const ThankYouPage: React.FC = () => {
  const navigate = useNavigate();
  const autoServicesBranding = isAutoServicesTenant(getTenantSubdomain());
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  useEffect(() => {
    const hasReloaded = sessionStorage.getItem('hasReloaded');
    if (!hasReloaded) {
      sessionStorage.setItem('hasReloaded', 'true');
      window.location.reload();
    } else {
      setAnimateIn(true);
    }
    // Cleanup function to reset the flag when component unmounts
    // return () => {
    //   sessionStorage.removeItem('hasReloaded');
    // };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Karbon",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 50%, #FFF3E6 100%)",
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(247, 147, 30, 0.15) 0%, rgba(247, 147, 30, 0) 70%)",
          animation: "float 6s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translate(0, 0)" },
            "50%": { transform: "translate(-30px, 30px)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-10%",
          left: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 87, 34, 0.12) 0%, rgba(255, 87, 34, 0) 70%)",
          animation: "float 8s ease-in-out infinite",
        }}
      />

      {/* Decorative Orange Accent Lines */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          left: "8%",
          width: "120px",
          height: "4px",
          bgcolor: "#F7931E",
          borderRadius: "2px",
          opacity: 0.5,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "20%",
          right: "10%",
          width: "90px",
          height: "4px",
          bgcolor: "#FF5722",
          borderRadius: "2px",
          opacity: 0.5,
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          py: 6,
          opacity: animateIn ? 1 : 0,
          transform: animateIn ? "translateY(0)" : "translateY(30px)",
          transition: "all 0.8s ease-out",
        }}
      >
        {/* Success Icon with Animation */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              bgcolor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 10px 40px rgba(247, 147, 30, 0.3)",
              position: "relative",
              animation: "scaleIn 0.6s ease-out",
              "@keyframes scaleIn": {
                "0%": { transform: "scale(0)", opacity: 0 },
                "50%": { transform: "scale(1.1)" },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
              "&::before": {
                content: '""',
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: "3px solid #F7931E",
                animation: "pulse 2s ease-out infinite",
              },
              "@keyframes pulse": {
                "0%": { transform: "scale(1)", opacity: 0.8 },
                "100%": { transform: "scale(1.3)", opacity: 0 },
              },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 70, color: "#F7931E" }} />
          </Box>
        </Box>

        {/* Thank You Message */}
        {/* Thank You Message */}
        <Typography
          variant="h2"
          sx={{
            fontFamily: "Karbon",
            fontWeight: 800,
            background: "linear-gradient(135deg, #F7931E 0%, #FF5722 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 2,
            textAlign: "center",
            fontSize: { xs: "2.5rem", md: "3.5rem" },
          }}
        >
          Request Received!
        </Typography>

        <Typography
          variant="h5"
          sx={{
            fontFamily: "Karbon",
            color: "#333",
            mb: 2,
            textAlign: "center",
            maxWidth: "700px",
            mx: "auto",
            lineHeight: 1.6,
            fontWeight: 600,
          }}
        >
          Thank you for reaching out to us!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontFamily: "Karbon",
            color: "#666",
            mb: 6,
            textAlign: "center",
            maxWidth: "650px",
            mx: "auto",
            lineHeight: 1.8,
            fontSize: "1.1rem",
          }}
        >
          We've received your enquiry and our dedicated team is already
          reviewing your information. We're excited to help you get started!
        </Typography>

        {/* Info Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 3,
            mb: 5,
            maxWidth: "1100px",
            mx: "auto",
          }}
        >
          {/* What Happens Next */}
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: 4,
              p: 4,
              textAlign: "center",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              border: "2px solid transparent",
              background:
                "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F7931E, #FF5722) border-box",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 12px 32px rgba(247, 147, 30, 0.2)",
              },
            }}
          >
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                bgcolor: "#FFF5EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 40, color: "#F7931E" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Karbon",
                fontWeight: 700,
                color: "#1a1a1a",
                mb: 2,
              }}
            >
              Quick Response
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Karbon",
                color: "#666",
                lineHeight: 1.7,
              }}
            >
              Our service advisor will contact you within{" "}
              <strong style={{ color: "#F7931E" }}>2 hours</strong> to confirm
              your appointment
            </Typography>
          </Box>

          {/* Personal Consultation */}
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: 4,
              p: 4,
              textAlign: "center",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              border: "2px solid transparent",
              background:
                "linear-gradient(white, white) padding-box, linear-gradient(135deg, #FF5722, #F7931E) border-box",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 12px 32px rgba(255, 87, 34, 0.2)",
              },
            }}
          >
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                bgcolor: "#FFF5EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <PhoneInTalkIcon sx={{ fontSize: 40, color: "#FF5722" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Karbon",
                fontWeight: 700,
                color: "#1a1a1a",
                mb: 2,
              }}
            >
              Expert Consultation
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Karbon",
                color: "#666",
                lineHeight: 1.7,
              }}
            >
              Get a detailed assessment of your vehicle's service needs from our
              certified mechanics
            </Typography>
          </Box>

          {/* Tailored Solution */}
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: 4,
              p: 4,
              textAlign: "center",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              border: "2px solid transparent",
              background:
                "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F7931E, #FF5722) border-box",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 12px 32px rgba(247, 147, 30, 0.2)",
              },
            }}
          >
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                bgcolor: "#FFF5EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 40, color: "#F7931E" }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Karbon",
                fontWeight: 700,
                color: "#1a1a1a",
                mb: 2,
              }}
            >
              Quality Service
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Karbon",
                color: "#666",
                lineHeight: 1.7,
              }}
            >
              Experience premium car care with genuine parts and professional
              workmanship guaranteed
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            mt: 5,
            mb: 5,
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/home")}
            sx={{
              py: 2.5,
              px: 7,
              background: "linear-gradient(135deg, #F7931E 0%, #FF5722 100%)",
              borderRadius: "12px",
              fontFamily: "Karbon",
              fontSize: "17px",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "0 8px 20px rgba(247, 147, 30, 0.4)",
              transition: "all 0.3s",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                transition: "left 0.5s",
              },
              "&:hover": {
                boxShadow: "0 10px 28px rgba(247, 147, 30, 0.5)",
                transform: "translateY(-3px)",
                "&::before": {
                  left: "100%",
                },
              },
            }}
          >
            Return to Home
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/customer/services")}
            sx={{
              py: 2.5,
              px: 7,
              borderColor: "#F7931E",
              borderWidth: 2.5,
              color: "#F7931E",
              borderRadius: "12px",
              fontFamily: "Karbon",
              fontSize: "17px",
              fontWeight: 700,
              textTransform: "none",
              transition: "all 0.3s",
              "&:hover": {
                borderColor: "#F7931E",
                borderWidth: 2.5,
                bgcolor: "#F7931E",
                color: "#FFFFFF",
                transform: "translateY(-3px)",
                boxShadow: "0 8px 20px rgba(247, 147, 30, 0.3)",
              },
            }}
          >
            View All Services
          </Button>
        </Box>

        {/* Contact Info - Enhanced Design */}
        <Box
          sx={{
            mt: 4,
            textAlign: "center",
            bgcolor: "#FFFFFF",
            borderRadius: 4,
            p: 4,
            boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
            maxWidth: "800px",
            mx: "auto",
            border: "1px solid rgba(247, 147, 30, 0.2)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Karbon",
              color: "#333",
              mb: 3,
              fontWeight: 700,
            }}
          >
            Need Urgent Assistance?
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 3,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  bgcolor: "#FFF5EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhoneInTalkIcon sx={{ color: "#F7931E", fontSize: 28 }} />
              </Box>
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Karbon",
                    color: "#999",
                    fontSize: "0.85rem",
                  }}
                >
                  Call Us
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Karbon",
                    color: "#F7931E",
                    fontWeight: 700,
                  }}
                >
                  1800-266-5337
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                width: "2px",
                height: "50px",
                bgcolor: "rgba(247, 147, 30, 0.2)",
                display: { xs: "none", sm: "block" },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  bgcolor: "#FFF5EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MailOutlineIcon sx={{ color: "#FF5722", fontSize: 28 }} />
              </Box>
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Karbon",
                    color: "#999",
                    fontSize: "0.85rem",
                  }}
                >
                  Email Us
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Karbon",
                    color: "#FF5722",
                    fontWeight: 700,
                    fontSize: "1rem",
                  }}
                >
                  {autoServicesBranding ? "connect@autoservices.in" : "connect@eurorepar.in"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={{
              fontFamily: "Karbon",
              color: "#999",
              mt: 3,
              fontSize: "0.9rem",
            }}
          >
            🔧 Open Monday - Saturday | 9:00 AM - 6:00 PM
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ThankYouPage;